import { DateTime } from "luxon";

import i18n from "../i18n";

export type ReplyByTone = "neutral" | "amber" | "red";

export interface ReplyByLabel {
  text: string;
  tone: ReplyByTone;
}

const AMBER_FROM_MINUTES = 120;
const WAITING_FROM_HOURS = 24;

const tr = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: "messages", ...options });

function formatDuration(minutes: number) {
  if (minutes < 60) return tr("{{minutes}}m", { minutes: Math.max(1, Math.floor(minutes)) });
  const hours = Math.floor(minutes / 60);
  const rest = Math.floor(minutes % 60);
  return hours < 10 && rest > 0 ? tr("{{hours}}h {{minutes}}m", { hours, minutes: rest }) : tr("{{hours}}h", { hours });
}

/**
 * The reply-by countdown on an inbox row: time left in grey, amber in the last two hours,
 * red once overdue. A day or more overdue switches to how long the buyer has been waiting,
 * which is the number that matters by then ("Waiting 3d").
 */
export function replyByLabel(replyBy: string, lastBuyerMessageAt: string, now: DateTime = DateTime.utc()): ReplyByLabel {
  const deadline = DateTime.fromISO(replyBy, { zone: "utc" });
  const minutesLeft = deadline.diff(now, "minutes").minutes;

  if (minutesLeft >= 0) {
    return {
      text: formatDuration(minutesLeft),
      tone: minutesLeft < AMBER_FROM_MINUTES ? "amber" : "neutral",
    };
  }

  const hoursOverdue = -minutesLeft / 60;
  if (hoursOverdue < WAITING_FROM_HOURS) {
    return { text: tr("Overdue {{time}}", { time: formatDuration(-minutesLeft) }), tone: "red" };
  }

  const daysWaiting = Math.floor(now.diff(DateTime.fromISO(lastBuyerMessageAt, { zone: "utc" }), "days").days);
  return { text: tr("Waiting {{days}}d", { days: daysWaiting }), tone: "red" };
}
